import { Box, Fade, Popover, Tooltip, Typography } from "@mui/material";
import { AddMemberModal } from "../add-member-modal";
import { EventTooltipActions } from "./event-tooltip-actions";
import { EventTooltipDetailsList } from "./event-tooltip-details-list";
import { EventTooltipHeader } from "./event-tooltip-header";
import type { DashboardCalendarEventTooltipProps } from "./types";
import { useCalendarEventTooltip } from "./use-calendar-event-tooltip";

const POPOVER_WIDTH = 372;

function DashboardCalendarEventTooltip({
  event,
  title,
  isOpen,
  onOpen,
  onClose,
}: DashboardCalendarEventTooltipProps) {
  const { datetime } = event;
  const {
    ambassador,
    client,
    anchorEl,
    open,
    hintOpen,
    setHintOpen,
    showAddMemberDialog,
    setShowAddMemberDialog,
    isLoadingUsers,
    isAmbassadorConfirmed,
    showSendReminder,
    isScheduledTimeExpired,
    hasActions,
    invitationRecordId,
    ambassadorName,
    clientName,
    zoomURL,
    handleClose,
    handleOpen,
    handleAddMemberClick,
  } = useCalendarEventTooltip({ event, isOpen, onOpen, onClose });

  return (
    <>
      <Tooltip
        title="Click to view meeting details"
        placement="top"
        arrow
        enterDelay={500}
        open={hintOpen && !open}
        onOpen={() => {
          if (!open) setHintOpen(true);
        }}
        onClose={() => setHintOpen(false)}
        disableFocusListener
        disableTouchListener
      >
        <Typography
          component="span"
          display="block"
          width="100%"
          onClick={handleOpen}
          pl={1}
          pt={0.2}
          fontSize={11}
          fontWeight={400}
          color="white"
          sx={{ cursor: "pointer" }}
        >
          <span className="event-dot mr3"></span>
          {title}
        </Typography>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        TransitionComponent={Fade}
        transitionDuration={180}
        marginThreshold={16}
        PaperProps={{
          elevation: 8,
          sx: {
            width: POPOVER_WIDTH,
            maxWidth: "calc(100vw - 24px)",
            borderRadius: 2.5,
            border: "1px solid rgba(226, 232, 240, 0.9)",
            overflow: "hidden",
            bgcolor: "#FFFFFF",
          },
        }}
      >
        <Box onClick={(e) => e.stopPropagation()}>
          <Box
            sx={{
              px: 2,
              py: 1.75,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #EEF2F7",
            }}
          >
            <EventTooltipHeader
              title={title}
              event={event}
              onClose={handleClose}
            />
          </Box>

          <EventTooltipDetailsList
            datetime={datetime}
            ambassadorName={ambassadorName}
            clientName={clientName}
            isLoadingUsers={isLoadingUsers}
            isAmbassadorConfirmed={isAmbassadorConfirmed}
          />

          {hasActions && (
            <EventTooltipActions
              zoomURL={zoomURL}
              showSendReminder={showSendReminder}
              isScheduledTimeExpired={isScheduledTimeExpired}
              invitationRecordId={invitationRecordId}
              additionalMembersCount={event.additionalMembers?.length}
              onAddMemberClick={handleAddMemberClick}
            />
          )}
        </Box>
      </Popover>

      {showAddMemberDialog && (
        <AddMemberModal
          event={{ ...event, ambassador, client }}
          initialMembers={event.additionalMembers}
          modalTitle={
            event.additionalMembers?.length ? "Edit Attendees" : "Add Members"
          }
          onClose={() => setShowAddMemberDialog(false)}
        />
      )}
    </>
  );
}

export { DashboardCalendarEventTooltip };
